// Account → Usage → "Spend detail" (REF-6975). One row per committed credit transaction from
// GET /v1/teams/usage-report/items, with the images it produced. Same guard as the usage report,
// so it only ever renders inside an already-authorised Usage tab. Contract rules that MUST hold:
//   • A `deleted: true` row gets the Deleted badge + a placeholder and NEVER an <img> — the BE sends
//     no url for deleted content, and the FE must not try to reconstruct or fetch one.
//   • `images[]` only ever holds live content; `deleted_image_count` reports the hidden rest of a
//     partly deleted row. A row with no images (non-image tool) renders without a gallery.
//   • Thumbnails are display-only (`previewUrl`); the viewer opens the ORIGINAL url
//     [DISPLAY_URL_NEVER_OUTBOUND]. Videos/archives never reach an <img> [VIDEO_URL_DETECTION].
//   • Every response re-checks it is still the latest request before writing — a slow page-1 reply
//     must not overwrite the page the user has since moved to [SELECTION_ANCHOR_BY_IDENTITY].
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, FileArchive, ImageOff, Info, Play } from "lucide-react";
import Select, { ISelectOption } from "components/Select";
import RetryImg from "components/RetryImg";
import ImagePopup from "components/ImagePopup";
import teamsService from "api/teams/teams.service";
import { ITeamUsageItem, ITeamUsageItemsRequest, ITeamUsageItemsResponse } from "api/teams/teams.type";
import { IconDownload } from "icons";
import { isNonImageUrl, isVideoUrl } from "utils/media";
import { previewUrl } from "utils/previewUrl";
import { objectTypeLabel } from "pages/Account/utils/usageLabels";
import { formatNumber } from "i18n/formatters/number";
import { reasonErrorText } from "utils/reasonError";
import i18n from "i18n";

const PAGE_SIZE = 25;
const MAX_THUMBS = 8;
const ALL = "all";
const DAY_MS = 86_400_000;
const DAY_FORMAT = "YYYY-MM-DD";
// BE contract: `from`/`to` are calendar dates in the viewer's `tz` (IANA), both inclusive, at most
// 366 days apart (400 beyond).
const MAX_RANGE_DAYS = 366;

// The viewer's IANA zone — sent with every request so the BE cuts days where the user's calendar does.
const viewerTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};
// Inclusive calendar-day count of a "YYYY-MM-DD" pair (both parsed as UTC midnight, so DST can't skew it).
const rangeDays = (from: string, to: string): number => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS) + 1;

type LoadState = "loading" | "ready" | "error";

// `tool` is an action_code. Same label catalog as the report's per-tool table; a code missing from it
// renders humanized (see utils/usageLabels).
const toolLabel = objectTypeLabel;

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;

  // Local time — the same zone (`tz`) the date filter is evaluated in.
  return new Intl.DateTimeFormat(i18n.language || "en", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(d);
};

const memberLabel = (m: { name?: string | null; email?: string | null }, fallback: string): string => m.name || m.email || fallback;

const DeletedPlaceholder = ({ label }: { label: string }) => (
  <div role="img" aria-label={label} className="flex-center w-14 h-14 rounded-md border border-dashed border-black-300 bg-body-200 text-grey" data-testid="spend-detail-deleted-placeholder">
    <ImageOff className="w-5 h-5" />
  </div>
);

const Thumb = ({ url, onOpen }: { url: string; onOpen: (url: string) => void }) => {
  const { t } = useTranslation();
  const tile = "flex-center w-14 h-14 rounded-md overflow-hidden border border-black-300 bg-body-200 flex-shrink-0";

  // Never hand a video or an archive to an <img>: open the original in a new tab instead.
  if (isVideoUrl(url) || isNonImageUrl(url)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="spend-detail-file"
        className={`${tile} text-grey hover:text-trueBlack dark:hover:text-white`}
        title={t("account:usage.spend.open_file")}
        aria-label={t("account:usage.spend.open_file")}
      >
        {isVideoUrl(url) ? <Play className="w-5 h-5" /> : <FileArchive className="w-5 h-5" />}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(url)}
      data-testid="spend-detail-thumb"
      className={`${tile} hover:opacity-80 transition-opacity`}
      title={t("account:usage.spend.open_image")}
      aria-label={t("account:usage.spend.open_image")}
    >
      <RetryImg src={previewUrl(url, { w: 128 }) ?? url} retries={1} alt={t("account:usage.spend.thumbnail_alt")} className="w-full h-full object-cover" loading="lazy" draggable={false} />
    </button>
  );
};

const ItemRow = ({ item, onOpen }: { item: ITeamUsageItem; onOpen: (url: string) => void }) => {
  const { t } = useTranslation();
  const shown = item.deleted ? [] : item.images.slice(0, MAX_THUMBS);
  const hiddenLive = item.deleted ? 0 : item.images.length - shown.length;
  // A project-level studio edit: no per-charge images exist, so a note replaces the gallery. A deleted
  // row still shows Deleted first.
  const unattributable = !item.deleted && item.images_attributable === false;

  return (
    <div className="flex flex-col gap-2.5 px-3 py-3 border-b border-black-300 last:border-b-0" data-testid="spend-detail-row" data-deleted={item.deleted ? "true" : "false"}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-bodySm text-trueBlack dark:text-white">
        <span className="text-grey tabular-nums">{formatDateTime(item.created_at)}</span>
        <span className="flex flex-col min-w-0">
          <span className="truncate">{memberLabel(item.member, t("account:usage.unknown_member"))}</span>
          {item.member.name && item.member.email && <span className="text-grey truncate">{item.member.email}</span>}
        </span>
        <span className="truncate">{toolLabel(item.tool)}</span>
        <span className="ml-auto tabular-nums">{t("account:usage.credits_count", { value: formatNumber(item.credits) })}</span>
      </div>

      {unattributable && (
        <span className="text-bodySm text-grey" data-testid="spend-detail-unattributable">
          {t("account:usage.spend.images_unattributable")}
        </span>
      )}

      {!unattributable && (item.deleted || item.images.length > 0 || item.deleted_image_count > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {item.deleted ? (
            <>
              <DeletedPlaceholder label={t("account:usage.spend.deleted_placeholder")} />
              {/* One readable line so an admin knows what was deleted: who, how many images, which tool. */}
              <span className="flex flex-wrap items-center gap-1.5 text-bodySm" data-testid="spend-detail-deleted-summary">
                <span className="px-2 py-0.5 rounded bg-body-200 text-grey border border-black-300" data-testid="spend-detail-deleted-badge">
                  {t("account:usage.spend.deleted_badge")}
                </span>
                <span className="text-grey">
                  {item.image_count > 0
                    ? t("account:usage.spend.deleted_detail", { count: item.image_count, tool: toolLabel(item.tool) })
                    : t("account:usage.spend.deleted_detail_no_images", { tool: toolLabel(item.tool) })}
                </span>
              </span>
            </>
          ) : (
            <>
              {shown.map((img, i) => (
                <Thumb key={`${img.url}-${i}`} url={img.url} onOpen={onOpen} />
              ))}
              {hiddenLive > 0 && <span className="text-bodySm text-grey">{t("account:usage.spend.more_images", { count: hiddenLive })}</span>}
              {item.deleted_image_count > 0 && <span className="text-bodySm text-grey">{t("account:usage.spend.deleted_images", { count: item.deleted_image_count })}</span>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const UsageSpendDetail = () => {
  const { t } = useTranslation();
  const [from, setFrom] = useState(() => dayjs().subtract(29, "day").format(DAY_FORMAT));
  const [to, setTo] = useState(() => dayjs().format(DAY_FORMAT));
  // The selected member's label travels with its id, so the option survives a range whose
  // `members` no longer lists that person.
  const [member, setMember] = useState<ISelectOption | null>(null);
  const [tool, setTool] = useState<string>(ALL);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ITeamUsageItemsResponse | null>(null);
  const [state, setState] = useState<LoadState>("loading");
  // The rejection itself, resolved to copy at render — so the fetch effect never depends on `t`.
  const [error, setError] = useState<unknown>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [openUrl, setOpenUrl] = useState<string | null>(null);
  const requestSeq = useRef(0);
  const [isExporting, setIsExporting] = useState(false);
  const [csvError, setCsvError] = useState(false);
  const [csvTruncated, setCsvTruncated] = useState(false);

  const today = dayjs().format(DAY_FORMAT);
  const rangeError = !from || !to || from > to ? "invalid_range" : rangeDays(from, to) > MAX_RANGE_DAYS ? "range_too_long" : null;
  const rangeInvalid = rangeError !== null;
  const memberId = member?.id;

  // The current filters as request params — shared by the list and the CSV, so the file is exactly
  // the filtered list on screen (minus paging).
  const filterParams = (): ITeamUsageItemsRequest => {
    const tz = viewerTimeZone();

    return { from, to, ...(tz ? { tz } : {}), ...(memberId !== undefined ? { member_id: memberId } : {}), ...(tool !== ALL ? { tool } : {}) };
  };

  useEffect(() => {
    if (rangeInvalid) return;

    const seq = ++requestSeq.current;
    const params = filterParams();
    setState("loading");

    (async () => {
      try {
        const res = await teamsService.getUsageReportItems({ ...params, page, page_size: PAGE_SIZE });
        if (seq !== requestSeq.current) return;

        // A 401 still resolves with _handled [HTTP_HANDLED_RESPONSE] (the session is ending). A 503
        // REJECTS for this path (HttpClient isSpendDetailRequest) so the catch below shows it inline,
        // with no "servers busy" modal on top.
        if (res?._handled || !res?.responseData) {
          setError(null);
          setState("error");

          return;
        }

        const payload = res.responseData;
        setData(payload);
        setState("ready");
        setCsvError(false);
      } catch (err: any) {
        if (seq !== requestSeq.current) return;
        setError(err);
        setState("error");
      }
    })();

    // Retire this request on any dep change (incl. the range turning invalid) and on unmount.
    return () => {
      requestSeq.current += 1;
    };
    // filterParams is a pure function of from/to/memberId/tool, all listed.
  }, [from, to, memberId, tool, page, reloadKey, rangeInvalid]);

  // CSV of the CURRENT filters (no paging; BE caps it at 1,000 rows for trial viewers, 10,000 otherwise). The file is fetched as a Blob
  // through the authenticated client, then saved via a temporary <a download> — same as Usage's Excel.
  const handleDownloadCsv = async () => {
    if (isExporting || rangeInvalid) return;
    const params = filterParams();
    setIsExporting(true);
    setCsvError(false);
    setCsvTruncated(false);
    try {
      const res = await teamsService.getUsageReportItemsCsv(params);
      // A 401 resolves to the `_handled` envelope, not a file [HTTP_HANDLED_RESPONSE]. A 503 rejects
      // (HttpClient isSpendDetailRequest) into the catch — inline error only, no global modal.
      if (!("file" in res) || !(res.file instanceof Blob)) {
        setCsvError(true);

        return;
      }
      const url = URL.createObjectURL(res.file);
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      link.download = `spend-detail_${params.from}_${params.to}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setCsvTruncated(res.truncated);
    } catch {
      setCsvError(true);
    } finally {
      setIsExporting(false);
    }
  };

  const memberOptions = useMemo<ISelectOption[]>(() => {
    // `members` = everyone with rows in the range, independent of the member/tool filters.
    const unknown = t("account:usage.unknown_member");
    const opts: ISelectOption[] = (data?.members ?? []).map((m) => ({ id: m.user_id, title: memberLabel(m, unknown) })).sort((a, b) => a.title.localeCompare(b.title));
    if (member && !opts.some((o) => o.id === member.id)) opts.unshift(member);

    return [{ id: ALL, title: t("account:usage.spend.all_members") }, ...opts];
  }, [data?.members, member, t]);

  const toolOptions = useMemo<ISelectOption[]>(() => {
    const tools = new Set(data?.tools ?? []);
    // Keep the active filter selectable even if the new range no longer lists it.
    if (tool !== ALL) tools.add(tool);

    return [{ id: ALL, title: t("account:usage.spend.all_tools") }, ...[...tools].map((code) => ({ id: code, title: toolLabel(code) }))];
  }, [data?.tools, tool, t]);

  // Any filter change restarts paging — page N of the old filter means nothing under the new one.
  const onFilter =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setPage(1);
      // CSV messages belong to the filters they were made for.
      setCsvError(false);
      setCsvTruncated(false);
    };
  const closeViewer = useCallback(() => setOpenUrl(null), []);

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const items = data?.items ?? [];

  return (
    <div className="flex flex-col gap-3" data-testid="spend-detail">
      <div className="flex flex-col">
        <span className="text-bodyMd text-trueBlack dark:text-white">{t("account:usage.spend.title")}</span>
        <span className="text-bodySm text-grey">{t("account:usage.spend.subtitle")}</span>
      </div>

      {/* Product note (REF-6975): the list excludes the legacy image generator (last used 2026-03-21) and
          counts saved results, so it is not expected to foot exactly to the report above. */}
      <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-body-100 border border-black-300 text-bodySm text-grey" data-testid="spend-detail-note">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{t("account:usage.spend.coverage_note")}</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Select
          options={memberOptions}
          defaultId={memberId ?? ALL}
          onChange={(o) => onFilter(setMember)(o.id === ALL ? null : o)}
          className="!w-[220px] !h-[34px] !items-center"
          searchable
          usePortal
          data-testid="spend-detail-member"
          titlePrefix={`${t("account:usage.spend.member")}: `}
        />
        <Select
          options={toolOptions}
          defaultId={tool}
          onChange={(o) => onFilter(setTool)(o.id)}
          className="!w-[220px] !h-[34px] !items-center"
          usePortal
          data-testid="spend-detail-tool"
          titlePrefix={`${t("account:usage.spend.tool")}: `}
        />
        <span className="inline-flex items-center gap-2 text-bodySm text-grey">
          <input
            type="date"
            aria-label={t("account:usage.spend.from")}
            data-testid="spend-detail-from"
            value={from}
            max={to || today}
            onChange={(e) => onFilter(setFrom)(e.target.value)}
            className="h-[34px] px-2 rounded-md border border-black-300 bg-body-100 text-bodySm text-trueBlack dark:text-white dark:[color-scheme:dark]"
          />
          <span>{t("account:usage.spend.to")}</span>
          <input
            type="date"
            aria-label={t("account:usage.spend.to_aria")}
            data-testid="spend-detail-to"
            value={to}
            min={from}
            max={today}
            onChange={(e) => onFilter(setTo)(e.target.value)}
            className="h-[34px] px-2 rounded-md border border-black-300 bg-body-100 text-bodySm text-trueBlack dark:text-white dark:[color-scheme:dark]"
          />
        </span>
        <button
          type="button"
          data-testid="spend-detail-csv"
          onClick={handleDownloadCsv}
          disabled={isExporting || rangeInvalid}
          className="ml-auto flex items-center gap-1.5 px-3 h-[34px] text-menu rounded-md border border-black-300 bg-body-100 text-trueBlack dark:text-white transition-colors hover:bg-body-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <IconDownload className="w-4 h-4" />
          {isExporting ? t("account:usage.spend.csv_in_progress") : t("account:usage.spend.csv_button")}
        </button>
      </div>

      {csvError && (
        <div className="p-4 bg-body-100 border border-black-300 rounded-md text-bodySm text-trueBlack dark:text-white" data-testid="spend-detail-csv-error" role="alert">
          {t("account:usage.spend.csv_error")}
        </div>
      )}

      {csvTruncated && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-body-100 border border-black-300 text-bodySm text-grey" data-testid="spend-detail-csv-truncated" role="status">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{t("account:usage.spend.csv_truncated")}</span>
        </div>
      )}

      {rangeInvalid ? (
        <div className="p-4 bg-body-100 border border-black-300 rounded-md text-bodySm text-grey">
          {rangeError === "range_too_long" ? t("account:usage.spend.range_too_long", { days: MAX_RANGE_DAYS }) : t("account:usage.spend.invalid_range")}
        </div>
      ) : state === "error" ? (
        <div className="flex items-center justify-between gap-3 p-4 bg-body-100 border border-black-300 rounded-md text-bodySm text-trueBlack dark:text-white" data-testid="spend-detail-error">
          <span>{error ? reasonErrorText(error, t("account:usage.spend.load_error")) : t("account:usage.spend.load_error")}</span>
          <button
            type="button"
            data-testid="spend-detail-retry"
            onClick={() => setReloadKey((k) => k + 1)}
            className="px-3 py-1.5 text-menu rounded-md border border-black-300 bg-body-100 hover:bg-body-200 transition-colors"
          >
            {t("account:usage.spend.retry")}
          </button>
        </div>
      ) : state === "loading" && !data ? (
        <div className="p-4 bg-body-100 border border-black-300 rounded-md text-bodySm text-grey" data-testid="spend-detail-loading">
          {t("account:usage.spend.loading")}
        </div>
      ) : items.length === 0 ? (
        <div className="p-4 bg-body-100 border border-black-300 rounded-md text-bodySm text-grey" data-testid="spend-detail-empty">
          {t("account:usage.spend.empty")}
        </div>
      ) : (
        <div className={state === "loading" ? "opacity-60 pointer-events-none transition-opacity" : "transition-opacity"}>
          <div className="flex flex-col rounded-md border border-black-300">
            {items.map((item) => (
              <ItemRow key={item.id} item={item} onOpen={setOpenUrl} />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-3 pt-3 text-bodySm text-grey">
            <span>{t("account:usage.spend.total", { count: total })}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                aria-label={t("account:usage.spend.prev")}
                data-testid="spend-detail-prev"
                className="flex-center w-8 h-8 rounded-md border border-black-300 bg-body-100 text-trueBlack dark:text-white hover:bg-body-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="tabular-nums">{t("account:usage.spend.page_info", { page, pages: pageCount })}</span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount}
                aria-label={t("account:usage.spend.next")}
                data-testid="spend-detail-next"
                className="flex-center w-8 h-8 rounded-md border border-black-300 bg-body-100 text-trueBlack dark:text-white hover:bg-body-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <ImagePopup image={openUrl} show={!!openUrl} onClose={closeViewer} hideDownload />
    </div>
  );
};

export default UsageSpendDetail;
